import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../../../utils/Auth';
import { ColorAndGradientToggles, ColorPickers, Rotation, cornersFrameTypes } from '../helper';
import styles from './CornerFrame.module.css';

const getInitialColor = (options, isSecondColor = false) => {
    const colorStops = options?.gradient?.colorStops;
    if (isSecondColor) {
        return colorStops?.[1]?.color || '#000000';
    }
    return options?.color || colorStops?.[0]?.color || '#000000';
};

// Main Component
export default function CornerFrame() {
    const { qr_options, optionsDispatch } = useAuth();
    const cornersSquareOptions = qr_options?.cornersSquareOptions;

    const colorType = useMemo(() => cornersSquareOptions?.gradient ? 'gradient' : 'single', [cornersSquareOptions?.gradient]);
    const gradientType = useMemo(() => cornersSquareOptions?.gradient?.type || 'linear', [cornersSquareOptions?.gradient?.type]);
    const firstColor = useMemo(() => getInitialColor(cornersSquareOptions), [cornersSquareOptions]);
    const secondColor = useMemo(() => getInitialColor(cornersSquareOptions, true), [cornersSquareOptions]);
    const rotation = useMemo(() => cornersSquareOptions?.gradient?.rotation || '0', [cornersSquareOptions?.gradient?.rotation]);
    
    const [ displayColorPicker, setDisplayColorPicker ] = useState(null);

    const handleCornersFrameTypeChange = (newType) => {
        optionsDispatch({ type: 'frame', options: { type: newType }});
    }

    const handleColorTypeChange = (e) => {
        const newColorType = e.target.checked ? 'gradient' : 'single';

        if (newColorType === 'single') {
            optionsDispatch({ type: 'frame', options: { color: firstColor, gradient: null }});
        } else {
            optionsDispatch({ type: 'frame', options: { color: null, gradient: { colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: secondColor }], type: gradientType }}});
        };
    };

    const handleGradientTypeChange = (e) => {
        const newGradientType = e.target.checked ? 'radial' : 'linear';
        optionsDispatch({ type: 'frame', options: { gradient: { ...cornersSquareOptions.gradient, type: newGradientType }}});
    };

    const handleColorChange = useCallback((color, name) => {
        if (colorType === 'single') {
            optionsDispatch({ type: 'frame', options: { color: color }});
        } else {
            if (name === 'single-color') {
                optionsDispatch({ type: 'frame', options: { gradient: { ...cornersSquareOptions.gradient, colorStops: [{ offset: 0, color: color }, { offset: 1, color: secondColor }]}}});
            } else if (name === 'gradient-color') {
                optionsDispatch({ type: 'frame', options: { gradient: { ...cornersSquareOptions.gradient, colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: color }]}}});
            };
        };
    },[colorType, firstColor, secondColor, cornersSquareOptions, optionsDispatch]);

    const handleRotationChange = (e) => {
        const rotation = e.target.value;
        optionsDispatch({ type: 'frame', options: { gradient: { ...cornersSquareOptions.gradient, rotation: rotation }}});
    };

    return (
        <div className={styles.container}>
            <div className={styles.inputGroup} style={{marginBottom: '0'}}>
                <div className={styles.label}>Frame Style</div>
                <div className={styles.cornersWrap}>
                    {cornersFrameTypes.map(type => (
                        <div key={type.value} className={cornersSquareOptions?.type === type.value ? `${styles.corners} ${styles.selected}` : styles.corners} onClick={() => handleCornersFrameTypeChange(type.value)}>
                            <img src={type.imgSrc} alt='type of corners' />
                            <p>{type.name}</p>
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.inputGroup} style={{flexDirection: 'row', gap: '0 1.25rem'}}>
                <ColorAndGradientToggles
                    colorType={colorType}
                    handleColorTypeChange={handleColorTypeChange}
                    gradientType={gradientType}
                    handleGradientTypeChange={handleGradientTypeChange}
                />
                <Rotation
                    show={colorType === 'gradient' && gradientType === 'linear'}
                    rotation={rotation}
                    handleRotationChange={handleRotationChange}
                />
            </div>
            <div className={styles.inputGroup} style={{marginBottom: '0'}}>
                <div className={styles.label}>Frames {colorType === 'single' ? 'Color' : 'Gradient'}</div>
                <ColorPickers
                    colorType={colorType}
                    firstColor={firstColor}
                    secondColor={secondColor}
                    handleColorChange={handleColorChange}
                    displayColorPicker={displayColorPicker}
                    setDisplayColorPicker={setDisplayColorPicker}
                />
            </div>
        </div>
    );
};